import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { Skeleton } from "@/components/ui/skeleton";
import type { SitePage } from "@shared/schema";
import { format } from "date-fns";
import { User, Calendar, Bot, ArrowLeft, Tag, Repeat2, Loader2, CheckCircle2, Copy, Check } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { apiRequest } from "@/lib/queryClient";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";

export default function BlogPost() {
  const [, params] = useRoute("/blog/:slug");
  const slug = params?.slug;

  const { data: post, isLoading } = useQuery<SitePage>({
    queryKey: [`/api/site-pages/${slug}`],
    queryFn: async () => {
      const res = await fetch(`/api/site-pages/${slug}`);
      if (!res.ok) throw new Error("Failed to fetch blog post");
      return res.json();
    }
  });

  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [repurposedPosts, setRepurposedPosts] = useState<{ platform: string; caption: string }[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const repurposeMutation = useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId || !slug) throw new Error("No workspace selected");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/repurpose-blog/${slug}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      setRepurposedPosts(data.posts || []);
      toast({ title: "Repurposed!", description: `${data.savedCount} social posts created and saved to your content queue.` });
    },
    onError: (err: any) => {
      toast({ title: "Repurpose Failed", description: err.message, variant: "destructive" });
    },
  });

  const copyText = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col">
        <SiteNav />
        <main className="flex-1 py-20 px-4">
          <div className="container mx-auto max-w-3xl">
            <Skeleton className="h-10 w-3/4 mb-6 bg-white/5" />
            <Skeleton className="h-6 w-1/4 mb-12 bg-white/5" />
            <Skeleton className="h-[400px] w-full mb-12 bg-white/5 rounded-3xl" />
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-4 w-full bg-white/5" />)}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col">
        <SiteNav />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
            <Link href="/blog">
              <Button variant="outline" className="border-white/10 text-white hover:bg-white/10">
                Back to Blog
              </Button>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const isAiAuthor = ["Peggy", "Draper", "Pete", "Rogers"].includes(post.author || "");
  const isDailyBrief = post.tags?.includes("daily-brief");

  return (
    <div className="min-h-screen bg-[#030308] flex flex-col">
      <SiteNav />

      <main className="flex-1 py-20 px-4">
        <article className="container mx-auto max-w-3xl">
          <Link href="/blog" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-bold uppercase tracking-widest mb-12 transition-colors group">
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> Back to Blog
          </Link>

          {isDailyBrief && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Draper's Daily Brief</span>
            </div>
          )}

          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-black text-white mb-8 tracking-tight leading-[1.15]" data-testid="text-post-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${isAiAuthor ? 'bg-blue-600/20 border-blue-500/30' : 'bg-white/5 border-white/10'}`}>
                  {isAiAuthor ? <Bot size={20} className="text-blue-400" /> : <User size={20} className="text-white/40" />}
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-none mb-1">{post.author || 'Drpr Team'}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/30">{isAiAuthor ? 'AI Agent' : 'Drpr Specialist'}</p>
                </div>
              </div>

              <div className="h-8 w-px bg-white/10" />

              <div className="flex items-center gap-2 text-white/40">
                <Calendar size={14} />
                <span className="text-sm font-medium">{format(new Date(post.publishedAt || post.createdAt!), 'MMMM d, yyyy')}</span>
              </div>
            </div>
          </header>

          {(post.coverImageUrl || (post as any).coverUrl) && (
            <div className="mb-12 rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-500/5">
              <img src={post.coverImageUrl || (post as any).coverUrl} alt={post.title} className="w-full h-auto max-h-[480px] object-cover" />
            </div>
          )}

          <div
            className="prose prose-invert max-w-none
              prose-headings:font-display prose-headings:font-black prose-headings:tracking-tight prose-headings:text-white
              prose-h1:text-4xl prose-h2:text-2xl prose-h2:text-white/90 prose-h2:border-b prose-h2:border-white/10 prose-h2:pb-3 prose-h2:mt-12
              prose-h3:text-lg prose-h3:text-white/80
              prose-p:text-white/65 prose-p:leading-relaxed prose-p:text-base
              prose-li:text-white/65 prose-li:leading-relaxed
              prose-strong:text-white prose-strong:font-bold
              prose-em:text-white/50 prose-em:text-sm
              prose-code:text-emerald-400 prose-code:bg-white/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
              prose-pre:bg-black/40 prose-pre:border prose-pre:border-white/10 prose-pre:rounded-2xl
              prose-blockquote:border-l-4 prose-blockquote:border-cyan-400/40 prose-blockquote:bg-cyan-400/5 prose-blockquote:px-6 prose-blockquote:py-2 prose-blockquote:rounded-r-xl prose-blockquote:not-italic
              prose-blockquote:text-white/60
              prose-a:text-blue-400 prose-a:no-underline hover:prose-a:text-blue-300 hover:prose-a:underline
              prose-hr:border-white/10
              prose-table:text-sm
              prose-thead:border-b prose-thead:border-white/10
              prose-th:text-white prose-th:font-bold prose-th:py-3 prose-th:text-left
              prose-td:text-white/60 prose-td:py-3 prose-td:border-b prose-td:border-white/5
              prose-img:rounded-2xl prose-img:border prose-img:border-white/10
              prose-ul:space-y-1 prose-ol:space-y-1"
            data-testid="text-post-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
            >
              {(post.content || "").replace(/^#\s+.+\n*/, "")}
            </ReactMarkdown>
          </div>

          {post.tags && post.tags.length > 0 && (
            <footer className="mt-16 pt-8 border-t border-white/5">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag size={14} className="text-white/20" />
                {post.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-widest text-white/40">
                    #{tag}
                  </span>
                ))}
              </div>
            </footer>
          )}

          {activeWorkspaceId && (
            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-purple-600/10 to-blue-600/5 border border-purple-500/15">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold text-white mb-1">Repurpose to Social</p>
                  <p className="text-xs text-white/40">Generate 4 platform-specific social posts from this blog post.</p>
                </div>
                <button
                  data-testid="button-repurpose-blog"
                  onClick={() => repurposeMutation.mutate()}
                  disabled={repurposeMutation.isPending}
                  className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-colors flex items-center gap-2"
                >
                  {repurposeMutation.isPending ? (
                    <><Loader2 size={14} className="animate-spin" /> Generating...</>
                  ) : (
                    <><Repeat2 size={14} /> Repurpose</>
                  )}
                </button>
              </div>

              {repurposedPosts.length > 0 && (
                <div className="space-y-3 mt-4 pt-4 border-t border-white/5" data-testid="repurposed-posts">
                  {repurposedPosts.map((p, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">{p.platform}</span>
                        <button
                          data-testid={`button-copy-repurpose-${i}`}
                          onClick={() => copyText(p.caption, `rp-${i}`)}
                          className="text-white/40 hover:text-white transition-colors flex items-center gap-1 text-xs"
                        >
                          {copiedKey === `rp-${i}` ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                          {copiedKey === `rp-${i}` ? "Copied" : "Copy"}
                        </button>
                      </div>
                      <p className="text-sm text-white/65 leading-relaxed whitespace-pre-wrap">{p.caption}</p>
                    </div>
                  ))}
                  <p className="text-[10px] text-white/30 text-center">
                    <CheckCircle2 size={10} className="inline mr-1" />
                    All posts saved to your content queue as drafts
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-blue-600/10 to-cyan-600/5 border border-blue-500/15">
            <p className="text-sm font-bold text-white mb-1">Want your own marketing agents?</p>
            <p className="text-xs text-white/40 mb-4">Draper publishes this brief automatically. Your agents can do the same for your brand.</p>
            <Link href="/join">
              <button className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-colors" data-testid="blog-cta-button">
                Deploy Your Agents →
              </button>
            </Link>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
